import { Directive, inject, Input, ViewChild } from '@angular/core';
import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';
import { IContactModel } from '../models/common';
import { FiUserSignInSettings } from '../../user/user-edit/fi-user-sign-in-settings';
import { takeUntil } from 'rxjs/operators';
import { FiUserSignInType } from '../fi-shared-services/fi-user-data.service';
import { Observable, Subject } from 'rxjs';
import { NgModel } from '@angular/forms';
import { isDefined } from '../utils';
import { DataFieldPropertyType } from '../../data-field/data-field-property-type';
import { FiCustomValidatorPatterns } from '../fi-custom-validator-patterns/fi-custom-validator-patterns';
import { ContactService } from './contact.service';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';

@Directive()
export abstract class FiContactBaseDirective extends ControlValueAccessorValidatorBase<IContactModel> {
    @Input() isRequired: boolean;
    @Input() isDisabled: boolean;
    @Input() fiUserSignInSettings: FiUserSignInSettings;
    @Input() onUpdateContactValidity$ = new Observable<IContactModel[]>();
    @Input() contacts: IContactModel[] = [];
    @Input() onDuplicationContactChange$ = new Observable<number[]>();

    contactInputModel: NgModel;
    isContactDuplicate: boolean;

    @ViewChild('contactInputModel') set _contactInputModel(value: NgModel) {
        if (isDefined(value)) {
            this.contactInputModel = value;
            this.subscribeOnControlStatusChange();
        }
    }

    contactService: ContactService = inject(ContactService);
    fiContactsService: FiContactsService = inject(FiContactsService);

    abstract dataFieldPropertyType: DataFieldPropertyType;
    beforeValidityUpdateFn?: () => void;

    ngOnInit(): void {
        this.fiUserSignInSettings?.updateSignInInputValidity$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((userSignInType: FiUserSignInType) => {
                this._onUpdateDataForUniqueContactCheck$.next();
                if (this.isInputValidityUpdateRequired(userSignInType)) this.updateInputValidity();
            });

        this.onDuplicationContactChange$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((duplicationUids: number[]) => {
            this.isContactDuplicate = !!duplicationUids?.length && duplicationUids.includes(this.ngModel.Uid);
            this._onUpdateDataForUniqueContactCheck$.next();
            this.updateInputValidity();
            this.cdr.markForCheck();
        });

        this.onUpdateContactValidity$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((contacts: IContactModel[]) => {
            this.contacts = [...contacts];
            this._onUpdateDataForUniqueContactCheck$.next();
            this.updateInputValidity();
        });
    }

    private isInputValidityUpdateRequired(userSignInType: FiUserSignInType): boolean {
        return (
            this.isContactTypeSignInType(userSignInType) ||
            this.fiUserSignInSettings.isPrevSignInContactCurrentType(this.isPhoneNumberContact())
        );
    }

    private _onUpdateDataForUniqueContactCheck$ = new Subject<void>();
    get updateDataForUniqueContactCheck$(): Observable<void> {
        return this._onUpdateDataForUniqueContactCheck$.asObservable();
    }

    private isContactTypeSignInType(userSignInType: FiUserSignInType): boolean {
        if (this.isPhoneNumberContact()) return userSignInType === FiUserSignInType.PhoneNumber;

        return userSignInType === FiUserSignInType.Email;
    }

    onContactChange() {
        this.doPropagateChanges();
        this.updateInputValidity();
    }

    private isPhoneNumberContact(): boolean {
        return this.dataFieldPropertyType === DataFieldPropertyType.PhoneNumber;
    }

    private updateInputValidity() {
        if (isDefined(this.beforeValidityUpdateFn)) {
            this.beforeValidityUpdateFn();
        }
        setTimeout(() => {
            this.contactInputModel.control.markAsTouched();
            this.contactInputModel.control.updateValueAndValidity();
        });
    }

    private subscribeOnControlStatusChange() {
        this.contactInputModel.control.statusChanges
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.cdr.markForCheck());
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    readonly FiCustomValidatorPatterns = FiCustomValidatorPatterns;
}
